import { useState } from 'react';
import {
  Baby,
  ChevronDown,
  ChevronUp,
  Database,
  GraduationCap,
  Landmark,
  Sparkles,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Card, CardContent } from '@/ui/base/card';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Progress } from '@/ui/base/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';
import { formatarDinheiro } from '@/nucleo/dinheiro';
import { cn } from '@/nucleo/utilitarios';

import type { ReservaSof } from './tipos';

const ICONE_CATEGORIA: Record<string, typeof GraduationCap> = {
  ESPECIAL: GraduationCap,
  INFANTIL: Baby,
  FUNDAMENTAL: Landmark,
};

const COR_CATEGORIA: Record<string, string> = {
  ESPECIAL: 'bg-orange',
  INFANTIL: 'bg-primary',
  FUNDAMENTAL: 'bg-success',
};

function fmtDataHora(iso: string) {
  return format(new Date(iso), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
}

/** Seção "Reserva orçamentária SOF" — saldo disponível por subfunção. */
export function ReservaOrcamentariaSof({ dados }: { dados: ReservaSof }) {
  const [expandido, setExpandido] = useState(false);

  return (
    <div className="space-y-3">
      <Card className="rounded-2xl border-primary/30 bg-linear-to-br from-primary/5 to-transparent">
        <CardContent className="pb-5 pt-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="shrink-0 rounded-xl bg-primary/15 p-2.5">
                <Database className="h-5 w-5 text-primary" aria-hidden />
              </div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  Reserva orçamentária SOF — total disponível
                </p>
                <p className="truncate text-3xl font-bold tabular-nums">
                  {formatarDinheiro(dados.totalReserva)}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {dados.qtdReservas} dotações ativas no programa TEG
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Badge variant="secondary" className="gap-1 text-[10px]">
                <Sparkles className="h-3 w-3" aria-hidden />
                Integração simulada
              </Badge>
              {dados.ultimaImportacao && (
                <span className="hidden text-[10px] text-muted-foreground sm:inline">
                  Última sinc: {fmtDataHora(dados.ultimaImportacao)}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {dados.categorias.map((categoria) => {
          const Icone = ICONE_CATEGORIA[categoria.codigo] ?? Landmark;
          const pct = dados.totalReserva > 0 ? (categoria.saldo / dados.totalReserva) * 100 : 0;
          return (
            <Card key={categoria.codigo} className="h-full rounded-2xl">
              <CardContent className="space-y-2 pb-5 pt-5">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div
                      className={cn(
                        'rounded-lg p-1.5',
                        COR_CATEGORIA[categoria.codigo] ?? 'bg-primary',
                      )}
                    >
                      <Icone className="h-4 w-4 text-primary-foreground" aria-hidden />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {categoria.rotulo}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[10px]">
                    {categoria.qtd}
                  </Badge>
                </div>
                <p className="truncate text-xl font-bold tabular-nums">
                  {formatarDinheiro(categoria.saldo)}
                </p>
                <div className="space-y-1">
                  <Progress value={pct} className="h-1.5" />
                  <p className="text-[10px] text-muted-foreground">{pct.toFixed(1)}% do total</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="rounded-2xl">
        <CardContent className="pb-3 pt-3">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-between text-xs"
            onClick={() => setExpandido((v) => !v)}
            aria-expanded={expandido}
          >
            <span className="font-medium">
              {expandido ? 'Ocultar' : 'Ver'} detalhamento por dotação ({dados.detalhamento.length})
            </span>
            {expandido ? (
              <ChevronUp className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronDown className="h-4 w-4" aria-hidden />
            )}
          </Button>
          {expandido && (
            <div className="mt-3 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Dotação</TableHead>
                    <TableHead className="text-xs">Subfunção</TableHead>
                    <TableHead className="text-xs">Conta</TableHead>
                    <TableHead className="text-right text-xs">Saldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dados.detalhamento.map((d) => (
                    <TableRow key={d.dotacao}>
                      <TableCell className="font-mono text-[11px]">{d.dotacao}</TableCell>
                      <TableCell className="text-xs">{d.subfuncao}</TableCell>
                      <TableCell className="font-mono text-[11px]">{d.conta}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {formatarDinheiro(d.saldo)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
